/*
  Pedlock header: the desktop menu row (Shop by Bike, Motorcycle Accessories
  …) folds away while scrolling down and comes back when scrolling up or near
  the top. The logo, search and icon row never changes size, and phones (no
  menu row) are left alone. Works on the theme's own sticky header (sticky
  type "Always") through CSS (.pl-header-compact in pedlock-main.css);
  nothing is duplicated.

  - One passive scroll listener, handled at most once per animation frame.
  - Small movements are ignored: it takes DOWN_TRAVEL px of downward scrolling
    to compact and UP_TRAVEL px of upward scrolling to restore.
  - The page never jumps: the height the header gives up is kept as margin
    under it. Both heights are measured up front and the margin animates with
    the same timing as the header, so the two always add up to the same total.
  - The theme is told about the new height (sticky filter bar, product
    gallery, menu drawer) once the change has settled.
  - Nothing changes while a menu or the search field is in use, or while a
    change is still settling (scroll movement the visitor didn't make is
    ignored).
*/
(() => {
  const TOP_ZONE = 140;
  const DOWN_TRAVEL = 24;
  const UP_TRAVEL = 64;
  const SETTLE_MS = 350;

  const init = () => {
    const header = document.getElementById('x-header');
    const content = document.getElementById('x-header-content');
    const section = header && header.closest('.shopify-section');
    if (!header || !content || !section || !header.hasAttribute('data-is-sticky')) return;
    if (section.dataset.plCompactReady) return;
    section.dataset.plCompactReady = 'true';

    const nav = content.querySelector('.header-bottom');
    let compact = false;
    let lastY = window.scrollY;
    let travel = 0;
    let queued = false;
    let settleTimer = null;
    let resizeTimer = null;
    let settledAt = 0;
    let navFull = 0;
    let foldable = null;

    // Height the header gives up when compact, measured with transitions off
    // (and the menu row unclamped, see .pl-header-measuring in the CSS)
    const measureSaving = () => {
      const wasCompact = section.classList.contains('pl-header-compact');
      section.classList.add('pl-header-measuring');
      section.classList.remove('pl-header-compact');
      const full = content.offsetHeight;
      navFull = nav ? nav.offsetHeight : 0;
      section.classList.add('pl-header-compact');
      const small = content.offsetHeight;
      section.classList.toggle('pl-header-compact', wasCompact);
      void content.offsetHeight;
      section.classList.remove('pl-header-measuring');
      return Math.max(0, full - small);
    };

    const busy = () => {
      if (content.querySelector('.toggle-menu.is-open')) return true;
      const active = document.activeElement;
      if (active && content.contains(active) && active.matches('input, textarea, select')) return true;
      const mobileNav = window.Alpine && window.Alpine.store && window.Alpine.store('xMobileNav');
      return !!(mobileNav && mobileNav.show);
    };

    const syncTheme = () => {
      const store = window.Alpine && window.Alpine.store && window.Alpine.store('xHeaderMenu');
      if (!store || typeof store.setHeaderHeight !== 'function') return;
      store.setHeaderHeight();
      store.setHeaderHeightVariable();
      store.setTopStickyHeader();
      store.setHeaderBottom();
    };

    // The menu row's height is pinned only while it folds or unfolds, so the
    // animation has a number to work with; the rest of the time it is left
    // free, so a change of screen size can never leave it clamped.
    const pinNavHeight = () => {
      if (!nav) return;
      section.style.setProperty('--pl-nav-height', navFull + 'px');
      void nav.offsetHeight;
    };

    const setCompact = (next) => {
      if (next === compact) return;
      // Nothing to fold away (phone widths have no menu row); remembered until
      // the window size changes, so scrolling on a phone measures nothing
      if (next && foldable === false) return;
      const saving = measureSaving();
      foldable = saving > 0;
      if (next && !foldable) return;
      pinNavHeight();
      compact = next;
      // Clips the menu row while it moves, so its links never draw over the
      // page below the header while the row is still opening
      section.classList.add('pl-header-folding');
      section.style.marginBottom = next ? saving + 'px' : '0px';
      section.classList.toggle('pl-header-compact', next);
      settledAt = performance.now() + SETTLE_MS;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        section.classList.remove('pl-header-folding');
        if (!compact) section.style.removeProperty('--pl-nav-height');
        syncTheme();
      }, SETTLE_MS);
    };

    const update = () => {
      queued = false;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      lastY = y;

      if (y < TOP_ZONE) {
        travel = 0;
        setCompact(false);
        return;
      }
      if (busy() || performance.now() < settledAt) {
        travel = 0;
        return;
      }
      if (delta === 0) return;
      if ((delta > 0) !== (travel > 0)) travel = 0;
      travel += delta;

      if (travel > DOWN_TRAVEL) setCompact(true);
      else if (travel < -UP_TRAVEL) setCompact(false);
    };

    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    // Layout changes with the window size: re-measure without animating
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (!compact) section.style.removeProperty('--pl-nav-height');
          const saving = measureSaving();
          foldable = saving > 0;
          if (compact) {
            section.classList.add('pl-header-measuring');
            if (!foldable) {
              // Now at phone width: no menu row left to fold, show the header as normal
              compact = false;
              section.classList.remove('pl-header-compact');
              section.style.removeProperty('--pl-nav-height');
            }
            section.style.marginBottom = compact ? saving + 'px' : '0px';
            void section.offsetHeight;
            section.classList.remove('pl-header-measuring');
          }
          syncTheme();
        }, 150);
      },
      { passive: true }
    );

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
